import React, { useEffect, useState, useRef, useMemo, memo } from "react";
import { PieChart as ChartSvg } from "react-minimal-pie-chart";
import { cn } from "../../styles";
import { Icon } from "../../atoms/Icon";
import { Typography } from "../../atoms/Typography";
import { Panel } from "../../layouts/Panel";
import { LocalizationScope } from "../../util/LocalizationScope";
import { ThemeColor } from "../../props";

const EPSILON = 0.0001;
const ANIM_SPEED = 0.005;
const LAG_CUTOFF = 120;
const ICON_SIZE_FT = 0.66;

export interface PieChartDataItem {
  localKey: string;
  color: string;
  value: number;
}

export interface PieChartProps {
  localKey: string;
  data: PieChartDataItem[];
  iconPath?: string;
  busy?: boolean;
  radius?: number;
  hideUnusedLabels?: boolean;
  animate?: boolean;
}

interface TooltipState {
  open: boolean;
  index: number;
  data: PieChartDataItem;
  x: number;
  y: number;
}

const stepValue = (
  current: number,
  target: number,
  delta: number,
): [number, boolean] => {
  if (delta < EPSILON) return [current, false];
  const diff = target - current;
  const absDiff = Math.abs(diff);
  if (absDiff < EPSILON) return [target, true];
  const change = delta * ANIM_SPEED * Math.sqrt(absDiff);
  if (absDiff < change) return [target, true];
  const dir = diff / absDiff;
  return [current + dir * change, false];
};

const stepData = (
  current: PieChartDataItem[],
  target: PieChartDataItem[],
  delta: number,
): [PieChartDataItem[], boolean] => {
  if (delta > LAG_CUTOFF) return [current, false];
  let done = true;
  const result = current.map((d, idx) => {
    const [vResult, vDone] = stepValue(d.value, target[idx].value, delta);
    if (!vDone) done = false;
    return { ...target[idx], value: vResult };
  });
  return [result, done];
};

interface ChartInnerProps {
  data: PieChartDataItem[];
  onUpdateTooltip: React.Dispatch<React.SetStateAction<TooltipState>>;
  animate: boolean;
}

function _Chart({ data, onUpdateTooltip, animate }: ChartInnerProps) {
  const [renderData, setRenderData] = useState(data);
  const requestRef = useRef<number>();
  const previousTimeRef = useRef<number>();
  const containerRef = useRef<HTMLDivElement>(null);
  // Store label positions in SVG viewBox coordinates (viewBox is 100x100)
  const labelPositions = useRef<{ x: number; y: number }[]>([]);

  const animateFn = (time: number) => {
    if (previousTimeRef.current === undefined) {
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animateFn);
    } else {
      const delta = time - previousTimeRef.current;
      previousTimeRef.current = time;
      setRenderData((d) => {
        const [result, done] = stepData(d, data, delta);
        if (!done) requestRef.current = requestAnimationFrame(animateFn);
        return result;
      });
    }
  };

  useEffect(() => {
    if (!animate) return;
    requestRef.current = requestAnimationFrame(animateFn);
    return () => cancelAnimationFrame(requestRef.current!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, animate]);

  return (
    <div ref={containerRef}>
      <ChartSvg
        data={renderData}
        lineWidth={50}
        labelPosition={75}
        startAngle={180}
        animate={animate}
        label={({ dataIndex, x, y, dx, dy }) => {
          labelPositions.current[dataIndex] = { x: x + dx, y: y + dy };
          return null;
        }}
        onMouseOver={(_, index) => {
          const svgEl = containerRef.current?.querySelector(
            "svg",
          ) as SVGSVGElement | null;
          const pos = labelPositions.current[index];
          if (!svgEl || !pos) return;
          // getScreenCTM converts SVG viewBox coords → screen/viewport coords,
          // correctly accounting for scaling, preserveAspectRatio, and transforms
          const ctm = svgEl.getScreenCTM();
          if (!ctm) return;
          const pt = svgEl.createSVGPoint();
          pt.x = pos.x;
          pt.y = pos.y;
          const screen = pt.matrixTransform(ctm);
          onUpdateTooltip((t) => ({
            ...t,
            open: true,
            index,
            data: data[index],
            x: screen.x,
            y: screen.y,
          }));
        }}
        onMouseOut={() => onUpdateTooltip((t) => ({ ...t, open: false }))}
      />
    </div>
  );
}
const Chart = memo(_Chart);

export function PieChart({
  localKey,
  data,
  iconPath,
  busy,
  radius = 90,
  hideUnusedLabels = true,
  animate = true,
}: PieChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState>({
    index: 0,
    open: false,
    data: data[0],
    x: 0,
    y: 0,
  });

  const localScope = useMemo(
    () => [localKey || "", "::PSS::PieChart"],
    [localKey],
  );

  return (
    <LocalizationScope scope={localScope}>
      <div className="flex items-stretch justify-between">
        <div className="flex px-4 py-2 items-start">
          <div className="flex flex-col mr-4">
            {data.map(
              (item, idx) =>
                (item.value > 0 || !hideUnusedLabels) && (
                  <div key={idx} className="mb-2 flex items-start">
                    <div
                      className="shrink-0 rounded-full mr-1 mt-[7px]"
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: item.color,
                      }}
                    />
                    <LocalizationScope scope={[item.localKey, ""]}>
                      <div>
                        <Typography.H5
                          local={{
                            key: "Title",
                            context: { value: item.value },
                          }}
                          noWrap
                          className="m-0"
                        />
                        <Typography.P
                          local={{
                            key: "Subtitle",
                            context: { value: item.value },
                          }}
                          color={ThemeColor.gray}
                          className="mb-0 text-sm"
                        />
                      </div>
                    </LocalizationScope>
                  </div>
                ),
            )}
          </div>
          <div
            className="relative shrink-0"
            style={{ width: radius * 2, height: radius * 2 }}
          >
            {iconPath && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Icon
                  path={iconPath}
                  size={radius * ICON_SIZE_FT}
                  color={ThemeColor.gray}
                  className={cn(busy && "animate-spin")}
                />
              </div>
            )}
            <Chart data={data} onUpdateTooltip={setTooltip} animate={animate} />
          </div>
        </div>
        {tooltip.open && tooltip.data && (
          <div
            className="pointer-events-none fixed z-[1200]"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: "translate(-50%, -50%)",
            }}
          >
            <Panel dense>
              <div className="flex items-start p-1">
                <div
                  className="shrink-0 rounded-full mr-1 mt-[7px]"
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: tooltip.data.color,
                  }}
                />
                <LocalizationScope scope={["Tooltip", ""]}>
                  <LocalizationScope scope={[tooltip.data.localKey, ""]}>
                    <Typography.H5
                      local={{
                        key: "Title",
                        context: { value: tooltip.data.value },
                      }}
                      noWrap
                      className="m-0"
                    />
                  </LocalizationScope>
                </LocalizationScope>
              </div>
            </Panel>
          </div>
        )}
      </div>
    </LocalizationScope>
  );
}
